import React from "react";
import { FaTruck, FaRupeeSign, FaMapMarkerAlt, FaClock, FaBoxOpen, FaHeadset } from "react-icons/fa";
import { MdLocalShipping } from "react-icons/md";

export default function ShippingPolicy() {
  return (
    <div className="mt-8 py-12 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl font-bold text-[#D4AF37] mb-8">Shipping Policy</h1>

        {/* Section 1: General Shipping Info */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaTruck className="mr-2 text-[#D4AF37]" /> Delivery Timeline
          </h3>
          <p className="text-sm mb-2">
            We deliver across <strong>all Indian states</strong>. Orders are processed and delivered within <strong>1-2 business days</strong> from the date of order and/or payment (excluding weekends and holidays), and typically arrive in:
          </p>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li><strong>Kerala:</strong> 2-3 business days</li>
            <li><strong>Rest of India:</strong> 3-7 business days</li>
          </ul>
          <p className="text-sm mt-2">
            Delivery timelines are subject to courier company norms. Delays may occur due to unforeseen circumstances (e.g., weather, logistics).
          </p>
        </div>

        {/* Section 2: Shipping Fees */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaRupeeSign className="mr-2 text-[#D4AF37]" /> Shipping Fees
          </h3>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li><strong>Kerala:</strong> Flat rate of ₹120</li>
            <li><strong>All Other States:</strong> Flat rate of ₹180</li>
          </ul>
          <p className="text-sm mt-2">
            Please note that shipping costs are non-refundable unless the return is due to our error.
          </p>
        </div>

        {/* Section 3: Shipping Partner & Process */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <MdLocalShipping className="mr-2 text-[#D4AF37]" /> Shipping Partner & Process
          </h3>
          <p className="text-sm mb-2">
            All orders are shipped through our trusted courier partner <strong>DTDC</strong> and/or speed post. Shipping details are regularly updated through our merchant panel to ensure accurate delivery information.
          </p>
          <h4 className="text-lg font-medium text-white mt-4 mb-2">DTDC Shipping Process:</h4>
          <ol className="list-decimal pl-6 text-sm space-y-2">
            <li><strong>Order Processing:</strong> Once your order is confirmed and payment is received, our team prepares your package within 1-2 business days.</li>
            <li><strong>Pickup:</strong> DTDC collects the package from our warehouse facility.</li>
            <li><strong>Shipment Creation:</strong> A unique tracking ID is generated and shared with you via SMS and email.</li>
            <li><strong>Transit:</strong> Your package is routed through DTDC's extensive network of sorting centers.</li>
            <li><strong>Out for Delivery:</strong> The package reaches your local DTDC hub and is assigned to a delivery agent.</li>
            <li><strong>Delivery:</strong> The delivery agent attempts delivery at your registered address. A signature may be required upon delivery. In case you're unavailable, they'll attempt delivery two more times.</li>
            <li><strong>Confirmation:</strong> Once delivered, you'll receive a delivery confirmation notification.</li>
          </ol>
          <p className="text-sm mt-2">
            DTDC provides real-time tracking updates that you can access through the tracking link provided.
          </p>
        </div>

        {/* Section 4: Tracking & Support */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaMapMarkerAlt className="mr-2 text-[#D4AF37]" /> Order Tracking
          </h3>
          <p className="text-sm mb-2">
            You'll receive a tracking link via SMS/Email once your order is dispatched. Use it to:
          </p>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>Track real-time location of your package.</li>
            <li>Estimate delivery date.</li>
            <li>Contact the courier partner if needed.</li>
          </ul>
          <p className="text-sm mt-2">
            Delivery confirmation will be sent to the email address provided during registration.
          </p>
        </div>

        {/* Section 5: Undelivered/Failed Deliveries */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaBoxOpen className="mr-2 text-[#D4AF37]" /> Failed Deliveries
          </h3>
          <p className="text-sm mb-2">
            If delivery fails due to incorrect address or recipient unavailability:
          </p>
          <ul className="list-disc pl-6 text-sm space-y-2">
            <li>Courier will attempt delivery <strong>2 more times</strong> (total of 3 attempts).</li>
            <li>After 3 failed attempts, the order returns to us, and a refund is issued (minus shipping fees unless the return is due to our error).</li>
          </ul>
        </div>

        {/* Section 6: Address & Responsibility */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaMapMarkerAlt className="mr-2 text-[#D4AF37]" /> Delivery Address
          </h3>
          <p className="text-sm">
            All orders will be delivered to the address provided by you at the time of purchase. We are not liable for any delays in delivery by the courier company or postal authority. Please ensure your address is complete and accurate.
          </p>
        </div>

        {/* Section 7: Remote Areas */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaClock className="mr-2 text-[#D4AF37]" /> Remote Areas
          </h3>
          <p className="text-sm">
            Deliveries to <strong>remote locations</strong> (e.g., hilly regions, islands) may take longer (typically 2-7 business days). Additional charges may apply—contact us for details.
          </p>
        </div>

        {/* Section 8: Customer Support */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <FaHeadset className="mr-2 text-[#D4AF37]" /> Customer Support
          </h3>
          <p className="text-sm">
            For any issues in utilizing our services or questions regarding your shipment, please contact our helpdesk at <span className="text-[#D4AF37]">support@mashoperfumes.com</span>.
          </p>
        </div>
      </div>
    </div>
  );
}